import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { WebBodyText16 } from '@fintronners/react-ui/src/GlobalStyling/webTypography';

import useAuthService from '@/hooks/api/useAuthService';
import { useSidePanel } from '@/context/SidePanelContext';
import { SheetFooter, SheetHeader, SheetTitle } from '@/components/common/Sheet';
import FormTextField from '@/components/common/Forms/fields/FormTextField';
import { Button } from '@/components/common/Button';
import { type UsernameData, usernameSchema } from '../schema';

export const UsernameForm = () => {
  const { closeSidePanel, goTo, sidePanelProps } = useSidePanel();
  const authService = useAuthService();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<UsernameData>({
    mode: 'onChange',
    resolver: yupResolver(usernameSchema),
    defaultValues: {
      username: sidePanelProps?.username || '',
    },
  });

  const onSubmit = async ({ username }: UsernameData) => {
    if (!isValid) {
      return;
    }

    try {
      await authService.authServiceForgotPassword({
        username,
      });

      goTo(1);
    } catch (error) {
      goTo(4);
    }
  };

  return (
    <form className="flex flex-col gap-5 h-full">
      <SheetHeader>
        <SheetTitle>{t('settings.security.changePassword.usernameForm.title')}</SheetTitle>
      </SheetHeader>
      <div className="flex flex-col h-full justify-between">
        <div>
          <WebBodyText16.Regular className="text-grey72 mb-5">
            {t('settings.security.changePassword.usernameForm.description')}
          </WebBodyText16.Regular>
          <FormTextField
            containerClassName="w-full"
            placeholder={t('settings.security.changePassword.usernameForm.usernamePlaceholder')}
            fieldError={errors.username}
            {...register('username')}
          />
        </div>
        <SheetFooter>
          <div className="flex flex-col justify-center items-center w-full gap-5">
            <Button
              className="w-full"
              variant="primary"
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting || !isValid}
            >
              {isSubmitting
                ? t('settings.security.changePassword.usernameForm.sending')
                : t('settings.security.changePassword.usernameForm.send')}
            </Button>
            <Button onClick={closeSidePanel} className="w-full" variant="secondary">
              {t('common.cancel')}
            </Button>
          </div>
        </SheetFooter>
      </div>
    </form>
  );
};
